import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Shield, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { vehicles } from '@/data/vehicles';

const VehicleShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % vehicles.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const currentVehicle = vehicles[activeIndex];

  return (
    <section className="pt-8 pb-8 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 z-10 relative">
        <div className="grid lg:grid-cols-2 gap-3 lg:gap-12 items-center">

          {/* Text Content */}
          <div className="order-2 lg:order-1 space-y-6 md:space-y-8 text-center lg:text-left">
            <div className="space-y-3 md:space-y-4">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                Premium Fleet
              </span>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Experience <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  {currentVehicle.name}
                </span>
              </h2>

              <p className="text-base md:text-lg text-gray-500 max-w-md mx-auto lg:mx-0">
                Enjoy a comfortable journey with our {currentVehicle.suitable.toLowerCase()}.
                Perfect for your travel needs with premium amenities.
              </p>
            </div>

            <div className="flex flex-wrap gap-0 justify-center lg:justify-start">
              <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 rounded-xl border border-gray-100">
                <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-xs md:text-sm font-medium text-gray-700">Top Rated</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 rounded-xl border border-gray-100">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500" />
                <span className="text-xs md:text-sm font-medium text-gray-700">Secure</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gray-50 rounded-xl border border-gray-100">
                <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-purple-500" />
                <span className="text-xs md:text-sm font-medium text-gray-700">{currentVehicle.ac ? 'AC Enabled' : 'Non-AC'}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 justify-center lg:justify-start">
              <Link to="/vehicles" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 hover:shadow-2xl hover:shadow-blue-500/40 hover:-translate-y-1 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 shadow-lg group"
                >
                  View Fleet
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>


            </div>
          </div>

          {/* Image Side */}
          <div className="order-1 lg:order-2 relative h-[250px] md:h-[350px] lg:h-[400px] w-full flex items-center justify-center p-4">

            {/* The Carve Card Container */}
            <div className="relative w-full h-full bg-slate-50/80 backdrop-blur-sm rounded-[2.5rem] overflow-hidden border border-white shadow-xl flex items-center justify-center">

              {/* Subtle inner background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-blue-50/20" />

              <div className="relative w-full h-full">
                {vehicles.map((vehicle, index) => (
                  <div
                    key={vehicle.id}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${index === activeIndex
                      ? 'opacity-100 translate-x-0'
                      : index < activeIndex
                        ? 'opacity-0 -translate-x-full'
                        : 'opacity-0 translate-x-full'
                      }`}
                  >
                    <img
                      src={vehicle.image}
                      alt={vehicle.name}
                      className="w-full h-full object-cover filter drop-shadow-xl"
                    />
                  </div>
                ))}
              </div>

              {/* Slide Indicators */}
              {/* <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20 bg-white/50 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                {vehicles.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`rounded-full transition-all duration-300 ${idx === activeIndex
                      ? 'w-3 h-3 bg-blue-600 scale-100'
                      : 'w-2 h-2 bg-gray-400 hover:bg-gray-500'
                      }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div> */}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default VehicleShowcase;
